import { h } from 'preact'
import clsx from 'clsx'
import Icon from '@/components/snippets/icon/icon'

interface PromolineData {
  title: string
  cta_url?: string
  text_colour: string
  priority: number
  end_date?: string
  countdown?: string[]
}

interface PromolineProps {
  promoline: PromolineData
  promolineLocation?: string
  promolineSize?: 'small'
  title?: string
  cta_url?: string
  text_colour?: string
  end_date?: string
  countdown?: string[]
}

export const Promoline = ({
  promoline,
  promolineLocation,
  promolineSize,
  title: titleOverride,
  cta_url: ctaUrlOverride,
  text_colour: textColourOverride,
  end_date: endDateOverride,
  countdown: countdownOverride,
  className
}: PromolineProps & { className?: string }) => {
  if (!promoline || !promoline.title) return null

  const title = titleOverride ?? promoline.title
  const ctaUrl = ctaUrlOverride ?? promoline.cta_url
  const textColour = textColourOverride ?? promoline.text_colour
  const endDate = endDateOverride ?? promoline.end_date
  const countdown = countdownOverride ?? promoline.countdown

  const countdownString = Array.isArray(countdown) ? countdown.join(',') : ''

  const shouldShowCountdown = (() => {
    if (!endDate || !countdownString || !promolineLocation) return false
    return countdownString.includes(promolineLocation)
  })()

  const getSizeConfig = () => {
    switch (promolineSize) {
      case 'small':
        return {
          container: 'w-fit',
          titleClass: 'caption items-center text-[--text-color] flex gap-1',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
    }
  }

  const sizeConfig = getSizeConfig()
  const Component = ctaUrl ? 'a' : 'div'

  const componentProps = ctaUrl ? {
    href: ctaUrl,
    'aria-label': title
  } : {}

  const CountdownTimer = ({
    endDate,
    layout = 'small'
  }: {
    endDate: string
    layout?: 'small' | 'medium' | 'large'
  }) => {
    if (!endDate) return null

    const getLayoutConfig = () => {
      switch (layout) {
        case 'small':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-0.125',
            itemClass: 'flex',
            numbersClass: 'caption',
            textClass: 'caption'
          }
        case 'medium':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-4 children:shrink-0',
            itemClass: 'flex flex-col gap-y-0.25 items-center justify-center text-center',
            numbersClass: 'h4 leading-none',
            textClass: 'font-ext text-xxs'
          }
        case 'large':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-5 children:shrink-0',
            itemClass: 'flex flex-col gap-y-0.5 items-center justify-center text-center',
            numbersClass: 'h2 leading-none',
            textClass: 'font-ext text-xs'
          }
      }
    }

    const config = getLayoutConfig()
    const labels = {
      small: ['d,', 'h,', 'm,', 's'],
      medium: ['Days', 'Hrs', 'Mins', 'Secs'],
      large: ['Days', 'Hrs', 'Mins', 'Secs']
    }
    const labelArray = labels[layout]

    return h(
      'x-countdown',
      {
        'data-end-date': new Date(endDate).toISOString(),
        'data-ui': 'visible',
        className: 'group/countdown shrink-0'
      },
      h('div', { className: config.wrapperClass },
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-days': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[0])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-hours': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[1])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-minutes': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[2])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-seconds': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[3])
        )
      )
    )
  }

  return (
    <Component
      {...componentProps}
      className={clsx(
        'shrink-0 flex',
        !shouldShowCountdown && 'flex-wrap',
        'items-center justify-between gap-x-0.5 gap-y-0.25',
        sizeConfig.container,
        'h-fit text-[--text-colour]',
        ctaUrl && 'link-fade',
        className
      )}
      style={{
        '--text-colour': textColour
      } as React.CSSProperties}
    >
      <div className={clsx(
        'grow flex gap-1',
        sizeConfig.container,
        shouldShowCountdown && sizeConfig.countdownPosition === 'block' ? 'flex-col' : 'items-center'
      )}>
        <Icon id='dot' className={'!size-3 shrink-0'} />
        <div className='flex items-center flex-wrap gap-y-1'>
          <span className={sizeConfig.titleClass}>
            {title}{shouldShowCountdown ? '\u00A0|\u00A0' : ''}
          </span>
          {shouldShowCountdown && (
            <CountdownTimer
              endDate={endDate}
              layout={sizeConfig.countdownLayout as 'small' | 'medium' | 'large'}
            />
          )}
        </div>
      </div>
    </Component>
  )
}
